import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Alert, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faWrench,
  faHome,
  faSignOutAlt,
  faCalendarAlt,
  faPhone,
  faUser,
  faEdit,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { maintenanceService } from '../../services/maintenanceService';
import { userService } from '../../services/userService';

const MaintenanceRequestDetails = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [request, setRequest] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({ status: '', notes: '' });
  const [updating, setUpdating] = useState(false);

  const maintenanceTypes = {
    plumbing: 'Plumbing Issues',
    electrical: 'Electrical Issues',
    hvac: 'HVAC/Heating & Cooling',
    appliance: 'Appliance Repair',
    structural: 'Structural Issues',
    pest_control: 'Pest Control',
    cleaning: 'Cleaning Services',
    landscaping: 'Landscaping',
    security: 'Security Issues',
    other: 'Other'
  };

  const priorityColors = {
    low: 'success',
    medium: 'warning',
    high: 'danger',
    urgent: 'danger'
  };

  const statusColors = {
    pending: 'warning',
    in_progress: 'info',
    completed: 'success',
    cancelled: 'secondary'
  };

  const statusLabels = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled'
  };

  useEffect(() => {
    fetchRequestDetails();
  }, [requestId]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      
      const profile = await userService.getUserProfile();
      setUserProfile(profile);
      
      const requestData = await maintenanceService.getMaintenanceRequestById(requestId);
      
      // Verify the request belongs to this company
      if (requestData.companyId !== profile.companyId) {
        setError('You do not have permission to view this request');
        return;
      }
      
      setRequest(requestData);
    } catch (error) {
      console.error('Error fetching request details:', error);
      setError('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleStatusUpdate = () => {
    setStatusUpdate({ 
      status: request.status, 
      notes: request.managerNotes || '' 
    });
    setShowStatusModal(true);
  };

  const confirmStatusUpdate = async () => {
    try {
      setUpdating(true);
      
      await maintenanceService.updateMaintenanceRequestStatus(
        request.id, 
        statusUpdate.status, 
        statusUpdate.notes
      );
      
      // Update local state
      setRequest(prev => ({
        ...prev,
        status: statusUpdate.status,
        managerNotes: statusUpdate.notes,
        updatedAt: new Date()
      }));
      
      setShowStatusModal(false);
      setStatusUpdate({ status: '', notes: '' });
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update request status');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          <FontAwesomeIcon icon={faWrench} className="me-2" />
          {error}
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/manager/maintenance')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Maintenance
        </Button>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          Maintenance request not found
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/manager/maintenance')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Maintenance
        </Button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            as={Link} 
            to="/manager/maintenance" 
            variant="outline-secondary" 
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Maintenance
          </Button>
          <div>
            <h2 className="mb-1">Maintenance Request Details</h2>
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon={faWrench} className="me-1" />
              {request.requestId}
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-warning" onClick={handleStatusUpdate}>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Update Status
          </Button>
          <Button as={Link} to="/dashboard" variant="outline-secondary">
            <FontAwesomeIcon icon={faHome} className="me-2" />
            Dashboard
          </Button>
          <Button variant="outline-danger" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
            Logout
          </Button>
        </div>
      </div>

      <Row>
        <Col lg={8}>
          {/* Request Information */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faWrench} className="text-primary me-2" />
                Request Information
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Request ID:</strong>
                    <div className="text-muted">{request.requestId}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Title:</strong>
                    <div className="text-muted">{request.title}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Type:</strong>
                    <div>
                      <Badge bg="secondary">
                        {maintenanceTypes[request.type] || request.type}
                      </Badge>
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Priority:</strong>
                    <div>
                      <Badge bg={priorityColors[request.priority]}>
                        {request.priority}
                      </Badge>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div>
                      <Badge bg={statusColors[request.status]}>
                        {statusLabels[request.status]}
                      </Badge>
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Unit Number:</strong>
                    <div className="text-muted">{request.unitNumber || 'N/A'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Created:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {request.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Last Updated:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {request.updatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Tenant Information */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUser} className="text-primary me-2" />
                Tenant Information
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Tenant Name:</strong>
                    <div className="text-muted">{request.tenantName}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Email:</strong>
                    <div className="text-muted">{request.tenantEmail}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Contact Phone:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faPhone} className="me-1" />
                      {request.contactPhone || 'N/A'}
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Preferred Time:</strong>
                    <div className="text-muted">{request.preferredTime || 'Any time'}</div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Description */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">Description</h5>
            </Card.Header>
            <Card.Body>
              <p className="mb-0">{request.description}</p>
            </Card.Body>
          </Card>

          {/* Additional Information */}
          {request.additionalNotes && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">Additional Notes</h5>
              </Card.Header>
              <Card.Body>
                <p className="mb-0">{request.additionalNotes}</p>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={4}>
          {/* Manager Response */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUser} className="text-primary me-2" />
                Manager Response
              </h5>
            </Card.Header>
            <Card.Body>
              {request.managerNotes ? (
                <p className="mb-0">{request.managerNotes}</p>
              ) : (
                <p className="text-muted mb-0">No response yet</p>
              )}
            </Card.Body>
          </Card>

          {/* Quick Actions */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button variant="outline-warning" onClick={handleStatusUpdate}>
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Update Status
                </Button>
                <Button as={Link} to="/manager/maintenance" variant="outline-secondary">
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back to Requests
                </Button>
                <Button as={Link} to="/dashboard" variant="outline-secondary">
                  <FontAwesomeIcon icon={faHome} className="me-2" />
                  Dashboard
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Status Update Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Request Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={statusUpdate.status}
                onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Manager Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={statusUpdate.notes}
                onChange={(e) => setStatusUpdate(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes or instructions for the tenant..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={confirmStatusUpdate}
            disabled={updating}
          >
            <FontAwesomeIcon icon={faSave} className="me-2" />
            {updating ? 'Updating...' : 'Update Status'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MaintenanceRequestDetails; 